//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import {
  BaseShopifyStoreCustomDomainRequest,
  ShopifyPricingRule,
  ShopifyPricingRuleRequest,
  ShopifyStoreInfoResponse,
  ShopifyStoreURLRequest,
  UpdateShopifyStoreCustomDomainRequest,
  UpdateShopifyStoreCustomDomainResponse,
} from '@apis/shopify/ShopifyApiModel';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

// TODO: PD-250821: Remove this constant once all
// endpoints are made available in the gateway
const PASSTHROUGH_DEFAULT = '/api/v1/pass-through';

// TODO: PD-250821: Remove this function once all
// endpoints are made available in the gateway
const passthroughFetch = (
  endpoint: string,
  init?: RequestInit,
  useFetchHeader?: boolean,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'affable_gateway',
    `${PASSTHROUGH_DEFAULT}${endpoint}`,
    init,
    useFetchHeader,
  );
};

/**
 * GET /api/soco/organization/store/basic
 *
 * Get Organization Shopify store info
 */
export const getShopifyStoreInfo = async (
  init?: RequestInit,
): Promise<ShopifyStoreInfoResponse> => {
  // TODO: PD-250821: Remove this function once all
  // endpoints are made available in the gateway
  const res = await passthroughFetch('/api/soco/organization/store/basic', {
    ...init,
    method: 'GET',
  });

  return res.json<ShopifyStoreInfoResponse>();
};

/**
 * GET /api/soco/organization/store/pricing-rules
 *
 * Get Organization Shopify pricing rules
 */
export const getShopifyPricingRules = async (
  req?: ShopifyPricingRuleRequest,
  init?: RequestInit,
): Promise<ShopifyPricingRule[]> => {
  // TODO: PD-250821: Remove this function once all
  // endpoints are made available in the gateway
  const res = await passthroughFetch(
    `/api/soco/organization/store/pricing-rules${makeParams({ search: req?.search })}`,
    {
      ...init,
      method: 'GET',
    },
  );
  return res.json<ShopifyPricingRule[]>();
};

/**
 * PUT /api/soco/organization/store/shop-url
 *
 * Set Organization Shopify store URL
 */
export const setShopifyStoreUrl = async (
  req: ShopifyStoreURLRequest,
  init?: RequestInit,
): Promise<ShopifyStoreInfoResponse> => {
  // TODO: PD-250821: Remove this function once all
  // endpoints are made available in the gateway
  const res = await passthroughFetch('/api/soco/organization/store/shop-url', {
    ...init,
    method: 'PUT',
    body: JSON.stringify(req),
  });

  return res.json<ShopifyStoreInfoResponse>();
};

/**
 * PATCH /api/soco/organization/store/custom-domains
 *
 * Update a Single Shopify Store Custom Domain of an Organization
 */
export const updateShopifyStoreCustomDomain = async (
  req: UpdateShopifyStoreCustomDomainRequest,
  init?: RequestInit,
): Promise<string | undefined> => {
  // TODO: PD-250821: Remove this function once all endpoints are made available in the gateway
  const res = await passthroughFetch(
    '/api/soco/organization/store/custom-domains',
    {
      ...init,
      method: 'PATCH',
      body: JSON.stringify(req),
    },
  );

  return (await res.json<UpdateShopifyStoreCustomDomainResponse>()).data;
};

/**
 * ADD /api/soco/organization/store/custom-domains
 *
 * Add Single Shopify Store Custom Domain to an Organization
 */
export const addShopifyStoreCustomDomain = async (
  req: BaseShopifyStoreCustomDomainRequest,
  init?: RequestInit,
): Promise<string | undefined> => {
  // TODO: PD-250821: Remove this function once all endpoints are made available in the gateway
  const res = await passthroughFetch(
    '/api/soco/organization/store/custom-domains',
    {
      ...init,
      method: 'POST',
      body: JSON.stringify(req),
    },
  );

  return (await res.json<UpdateShopifyStoreCustomDomainResponse>()).data;
};

/**
 * DELETE /api/soco/organization/store/custom-domains/:customDomain
 *
 * Delete a Single Shopify Store Custom Domain
 */
export const deleteShopifyStoreCustomDomain = async (
  req: BaseShopifyStoreCustomDomainRequest,
  init?: RequestInit,
): Promise<void> => {
  // TODO: PD-250821: Remove this function once all endpoints are made available in the gateway
  await passthroughFetch(
    `/api/soco/organization/store/custom-domains/${req.customDomain}`,
    {
      ...init,
      method: 'DELETE',
    },
  );
};
